import { printOrder, unPrintOrder } from '@/api/order-api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { useOrderReceipt } from '@/services/order-service';
import { OrderItemResponse } from '@/types/order-type';
import { Customer } from '@/types/customer-type';
import { FileText, Loader2, RotateCcw } from 'lucide-react';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { ReceiptCustomer } from './receipt/receipt-customer';
import { ReceiptFooter } from './receipt/receipt-footer';
import { ReceiptHeader } from './receipt/receipt-header';
import { ReceiptItems } from './receipt/receipt-items';
import { ReceiptStyles } from './receipt/receipt-styles';
import { ReceiptTotals } from './receipt/receipt-totals';

// Items that fit on the first page (which also carries header/customer/totals).
const ITEMS_PER_FIRST_PAGE = 10;
// Items that fit on continuation pages (items table only).
const ITEMS_PER_SUBSEQUENT_PAGE = 18;

function paginateItems(
  items: OrderItemResponse[],
  firstPageSize: number,
  subsequentPageSize: number,
): OrderItemResponse[][] {
  if (items.length === 0) return [];
  const pages: OrderItemResponse[][] = [];
  pages.push(items.slice(0, firstPageSize));
  for (let i = firstPageSize; i < items.length; i += subsequentPageSize) {
    pages.push(items.slice(i, i + subsequentPageSize));
  }
  return pages;
}

type ReceiptViewModel = {
  store: any;
  order: any;
  orderItems: OrderItemResponse[];
  customer?: Customer;
  receiptNo: string;
  date: Date;
  totalQuantity: number;
  subtotal: number;
  discount: number;
  tax: number;
  shippingFee: number;
  total: number;
  paymentMethod?: string;
};

// ---- Calculation helpers ----------------------------------------------------

function calculateSubtotal(orderItems: OrderItemResponse[]): number {
  return orderItems.reduce((acc: number, item) => {
    const quantity = Number(item.quantity ?? 0);
    const price =
      typeof item.price === 'number'
        ? item.price
        : Number(item.price ?? 0);

    const lineAmount =
      item.amount != null ? Number(item.amount) : quantity * price;

    return acc + (Number.isFinite(lineAmount) ? lineAmount : 0);
  }, 0);
}

function calculateTotalQuantity(orderItems: OrderItemResponse[]): number {
  return orderItems.reduce((acc: number, item) => {
    const quantity = Number(item.quantity ?? 0);
    return acc + (Number.isFinite(quantity) ? quantity : 0);
  }, 0);
}

function calculateDiscount(subtotal: number, order: any): number {
  if (order?.discountTotal != null) {
    return Number(order.discountTotal);
  }

  if (order?.discountPercentage) {
    return (Number(subtotal) * Number(order.discountPercentage)) / 100;
  }

  return 0;
}

function calculateTaxableAmount(
  subtotal: number,
  shippingFee: number,
  discount: number,
): number {
  return subtotal + shippingFee - discount;
}

function calculateTax(taxableAmount: number, taxRatePercent: number): number {
  const taxRate = Number(taxRatePercent ?? 0) / 100;
  return taxableAmount * taxRate;
}

function calculateTotal(
  order: any,
  subtotal: number,
  shippingFee: number,
  discount: number,
  tax: number,
): number {
  if (order?.totalAmount != null) {
    return Number(order.totalAmount);
  }

  return subtotal + shippingFee - discount + tax;
}

export type ReceiptProps = {
  orderId: string;
  footerNote?: string;
};

export const Receipt = forwardRef<HTMLDivElement, ReceiptProps>(function Receipt(
  { orderId, footerNote = 'Thank you for your purchase!' },
  ref,
) {
  const { data: receiptData, isLoading, error } = useOrderReceipt(orderId);

  // Calculate derived values from receipt data
  // Build a simple view model that the printable receipt components can use.
  const calculatedValues: ReceiptViewModel | null = useMemo(() => {
    if (!receiptData) {
      return null;
    }

    const { store, order, orderItems, fulfillment } = receiptData;

    if (!store || !order || !orderItems) {
      return null;
    }

    // ---- Quantities & money values -----------------------------------------

    const subtotalSource = calculateSubtotal(orderItems);
    const subtotal = order?.subTotal ?? subtotalSource;

    const totalQuantity = calculateTotalQuantity(orderItems);

    const shippingFee = Number(
      fulfillment?.price ?? order?.shippingFee ?? order?.deliveryFee ?? 0,
    );

    const discount = calculateDiscount(subtotal, order);
    const taxableAmount = calculateTaxableAmount(subtotal, shippingFee, discount);
    const tax = calculateTax(taxableAmount, order?.taxRate ?? 0);
    const total = calculateTotal(order, subtotal, shippingFee, discount, tax);

    // ---- Lightweight customer view ----------------------------------------

    const customer: Customer | undefined = order.customerName
      ? ({
          id: order.customerId,
          name: order.customerName,
          phone: order.shippingPhone || null,
          address: order.shippingAddress || null,
        } as Customer)
      : undefined;

    return {
      store,
      order,
      orderItems,
      customer,
      receiptNo: order.orderNumber || order.id,
      date: order.createdAt ? new Date(order.createdAt) : new Date(),
      totalQuantity,
      subtotal,
      discount,
      tax,
      shippingFee,
      total,
      paymentMethod: order.paymentMethod || undefined,
    };
  }, [receiptData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !receiptData || !calculatedValues) {
    return (
      <div className="flex items-center justify-center p-8 text-red-500">
        <p>Failed to load receipt data</p>
      </div>
    );
  }

  return (
    <ReceiptDocument
      ref={ref}
      viewModel={calculatedValues}
      footerNote={footerNote}
    />
  );
});

type ReceiptDocumentProps = {
  viewModel: ReceiptViewModel;
  footerNote?: string;
};

const ReceiptDocument = forwardRef<HTMLDivElement, ReceiptDocumentProps>(
  function ReceiptDocument({ viewModel, footerNote }: ReceiptDocumentProps, ref) {
    const shouldShowPrices = viewModel.order.cashOnDelivery === true;

    return (
      <>
        <ReceiptStyles />
        <div
          ref={ref ?? undefined}
          className="receipt-container mx-auto max-w-md bg-white text-xs border rounded-lg shadow-sm print:border-none print:shadow-none"
        >
          <ReceiptHeader
            company={viewModel.store}
            date={viewModel.date}
            receiptNo={viewModel.receiptNo}
          />
          <ReceiptCustomer
            customer={viewModel.customer}
            paymentMethod={viewModel.paymentMethod}
            shippingAddress={viewModel.order.shippingAddress}
          />
          <ReceiptItems
            items={viewModel.orderItems ?? []}
            showPrices={shouldShowPrices}
          />
          <ReceiptTotals
            totalQuantity={viewModel.totalQuantity}
            subtotal={viewModel.subtotal}
            discount={viewModel.discount}
            tax={viewModel.tax}
            shippingFee={viewModel.shippingFee}
            total={viewModel.total}
            showPrices={shouldShowPrices}
          />
        </div>
      </>
    );
  },
);

export function ReceiptPreviewCard({ orderId, footerNote }: ReceiptProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isUnprinting, setIsUnprinting] = useState(false);
  const { data: receiptData } = useOrderReceipt(orderId);

  const receiptNo = receiptData?.order?.orderNumber || receiptData?.order?.id || '';

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Receipt #${receiptNo}`,
    pageStyle: `
      @page {
        size: 100mm 150mm;
        margin: 0;
        margin-bottom: 6mm;
        @bottom-right { content: "Page " counter(page); font-size: 10px; color: #6b7280; text-align: right; padding-right: 3mm; }
      }
    `,
    // Mark the order as printed before the browser print dialog opens.
    onBeforePrint: async () => {
      setIsPrinting(true);

      try {
        const response = await printOrder(orderId);
        if (!response.success) {
          throw new Error(response.message);
        }
      } catch (error) {
        console.log('track printing error', error);
        toast({ title: 'Error', description: 'Error during mark as printed' });
        setIsPrinting(false);
        throw error;
      }
    },
    // Reset loading state once printing has completed or been cancelled.
    onAfterPrint: () => {
      setIsPrinting(false);
    },
    onPrintError: (errorLocation, error) => {
      console.error('Print error', errorLocation, error);
      toast({ title: 'Error', description: 'Error printing receipt' });
      setIsPrinting(false);
    },
  });

  const handleUnprint = async () => {
    setIsUnprinting(true);
    try {
      const resp = await unPrintOrder(orderId);
      if (!resp.success) {
        throw new Error(resp.message);
      } else {
        toast({
          title: 'Success',
          description: 'Successfully mark as un-printed',
        });
      }
    } catch (err) {
      console.log('track printing error', err);
      toast({ title: 'Error', description: 'Error during mark as un-printed' });
    } finally {
      setIsUnprinting(false);
    }
  };

  // Disable actions while a print / un-print request is in flight.
  const isAnyActionLoading = isPrinting || isUnprinting;

  return (
    <Card className="max-h-[90vh] overflow-y-auto">
      <CardHeader className="pb-2 flex items-center justify-between gap-5">
        <CardTitle>Receipt Preview</CardTitle>
        <TooltipProvider>
          <div className="no-print mt-3 flex items-center gap-2">
            {/* Primary Actions - Print Options */}
            <div className="flex items-center gap-2 border-r pr-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    className="gap-2"
                    onClick={handlePrint}
                    disabled={isAnyActionLoading}
                  >
                    {isPrinting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                    {isPrinting ? 'Printing...' : 'Print / PDF'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Print receipt or save as PDF</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Secondary Action */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-muted-foreground hover:text-foreground"
                  onClick={handleUnprint}
                  disabled={isAnyActionLoading}
                >
                  {isUnprinting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                  {isUnprinting ? 'Updating...' : 'Unprint'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Mark receipt as un-printed</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </CardHeader>

      <CardContent>
        <Receipt ref={printRef} orderId={orderId} footerNote={footerNote} />
      </CardContent>
    </Card>
  );
}
